import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/hooks/useOrganization";
import { logAuthEvent } from "@/lib/auditLog";

const WARNING_MS = 2 * 60 * 1000; // 2 minute warning
const EVENTS = ["mousemove", "keydown", "click", "touchstart", "scroll"] as const;

export function useIdleTimeout(): void {
  const { user } = useAuth();
  const { data: org } = useOrganization();
  const timeoutMinutes = (org as { idle_timeout_minutes?: number } | null | undefined)?.idle_timeout_minutes ?? 30;

  const idleTimer = useRef<number | null>(null);
  const warningTimer = useRef<number | null>(null);
  const toastId = useRef<string | number | null>(null);

  useEffect(() => {
    if (!user) return;

    const totalMs = timeoutMinutes * 60 * 1000;
    const warnAt = Math.max(totalMs - WARNING_MS, 30 * 1000);

    const clearTimers = () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      if (warningTimer.current) window.clearTimeout(warningTimer.current);
      idleTimer.current = null;
      warningTimer.current = null;
    };

    const signOutNow = async () => {
      logAuthEvent("signed_out", { details: { reason: "idle_timeout" } });
      if (toastId.current) toast.dismiss(toastId.current);
      await supabase.auth.signOut();
      toast.error("Signed out due to inactivity");
    };

    const showWarning = () => {
      toastId.current = toast.warning("You'll be signed out soon", {
        description: "You've been inactive. Click to stay signed in.",
        duration: WARNING_MS,
        action: { label: "Stay signed in", onClick: () => reset() },
      });
    };

    const reset = () => {
      if (toastId.current) {
        toast.dismiss(toastId.current);
        toastId.current = null;
      }
      clearTimers();
      warningTimer.current = window.setTimeout(showWarning, warnAt);
      idleTimer.current = window.setTimeout(signOutNow, totalMs);
    };

    const handler = () => {
      // If warning is up, ignore activity until user clicks the button
      if (toastId.current) return;
      reset();
    };

    EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    reset();

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, handler));
      clearTimers();
      if (toastId.current) toast.dismiss(toastId.current);
    };
  }, [user, timeoutMinutes]);
}
